import React, { useEffect, useState } from 'react';
import { Vendor } from '../../utils/constants';
import venueSettingsService from '../../services/venueSettingsService';
import styles from './VendorDetailsForm.module.css';

export interface VendorDetailsFormData {
  name: string;
  cuisine_type: string | null;
  description: string | null;
  image: string | null;
  status: 'active' | 'inactive';
}

interface VendorDetailsFormProps {
  vendor: Vendor;
  formId?: string;
  hideFooter?: boolean;
  onValidityChange?: (valid: boolean) => void;
  onSubmit: (data: VendorDetailsFormData) => void;
  onCancel: () => void;
}

interface FormErrors {
  name?: string;
  image?: string;
  description?: string;
}

const CUISINE_OPTIONS = [
  { id: 'burgers', label: 'Burgers', icon: '🍔' },
  { id: 'pizza', label: 'Pizza', icon: '🍕' },
  { id: 'chicken', label: 'Chicken', icon: '🍗' },
  { id: 'mexican', label: 'Mexican', icon: '🌮' },
  { id: 'asian', label: 'Asian', icon: '🍜' },
  { id: 'drinks', label: 'Drinks', icon: '🥤' },
  { id: 'snacks', label: 'Snacks', icon: '🍿' },
  { id: 'other', label: 'Other', icon: '🍽️' },
];

function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

const VendorDetailsForm: React.FC<VendorDetailsFormProps> = ({
  vendor,
  formId = 'vendor-details-form',
  hideFooter = false,
  onValidityChange,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState(vendor.name || '');
  const [cuisineType, setCuisineType] = useState(vendor.cuisine_type || '');
  const [description, setDescription] = useState(vendor.description || '');
  const [image, setImage] = useState<string | null>(vendor.image || null);
  const [status, setStatus] = useState<'active' | 'inactive'>(
    (vendor.status || 'active') === 'active' ? 'active' : 'inactive'
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [cuisineOptions, setCuisineOptions] = useState(CUISINE_OPTIONS);

  useEffect(() => {
    setName(vendor.name || '');
    setCuisineType(vendor.cuisine_type || '');
    setDescription(vendor.description || '');
    setImage(vendor.image || null);
    setStatus((vendor.status || 'active') === 'active' ? 'active' : 'inactive');
    setErrors({});
  }, [vendor]);

  useEffect(() => {
    venueSettingsService.get().then((data) => {
      if (data.cuisineTypes?.length) {
        setCuisineOptions(
          data.cuisineTypes.map((c) => ({
            id: c.id,
            label: c.label,
            icon: c.icon || '🍽️',
          }))
        );
      }
    }).catch(() => {
      // keep defaults
    });
  }, []);

  const isFormValid =
    name.trim().length >= 2 &&
    name.trim().length <= 50 &&
    (!image || isValidUrl(image));

  useEffect(() => {
    onValidityChange?.(isFormValid);
  }, [isFormValid, onValidityChange]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    const trimmed = name.trim();

    if (!trimmed) {
      next.name = 'Vendor name is required';
    } else if (trimmed.length < 2) {
      next.name = 'Vendor name must be at least 2 characters';
    } else if (trimmed.length > 50) {
      next.name = 'Vendor name must be less than 50 characters';
    }

    if (image && !isValidUrl(image)) {
      next.image = 'Enter a valid image URL';
    }

    if (description.length > 500) {
      next.description = 'Description must be 500 characters or less';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      name: name.trim(),
      cuisine_type: cuisineType || null,
      description: description.trim() || null,
      image: image?.trim() || null,
      status,
    });
  };

  const avatarFallback = name.trim().charAt(0).toUpperCase() || '?';

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit}>
      <p className={styles.intro}>
        Update how this vendor appears to admins and customers. Section assignments are edited separately.
      </p>

      <div className={styles.field}>
        <label className={`${styles.label} ${styles.labelRequired}`} htmlFor="vendor-name">
          Vendor name
        </label>
        <input
          id="vendor-name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
          }}
          maxLength={50}
          className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
          placeholder="e.g. Stadium Grill"
        />
        {errors.name && <p className={styles.error}>{errors.name}</p>}
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="vendor-cuisine">
          Cuisine type
        </label>
        <select
          id="vendor-cuisine"
          value={cuisineType}
          onChange={(e) => setCuisineType(e.target.value)}
          className={styles.input}
        >
          <option value="">— Select cuisine type —</option>
          {cuisineOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.icon} {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="vendor-description">
          Description
        </label>
        <textarea
          id="vendor-description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            if (errors.description) setErrors((prev) => ({ ...prev, description: undefined }));
          }}
          maxLength={500}
          rows={3}
          className={`${styles.textarea} ${errors.description ? styles.inputError : ''}`}
          placeholder="Optional short description for this vendor"
        />
        {errors.description && <p className={styles.error}>{errors.description}</p>}
      </div>

      <section className={styles.photoSection}>
        <h3 className={styles.sectionTitle}>Logo / photo</h3>
        <div className={styles.imagePreview}>
          {image ? (
            <img
              src={image}
              alt=""
              className={styles.imageThumb}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <span className={styles.imageFallback}>{avatarFallback}</span>
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="vendor-image-url">
            Image URL
          </label>
          <input
            id="vendor-image-url"
            type="url"
            value={image || ''}
            onChange={(e) => {
              setImage(e.target.value.trim() || null);
              if (errors.image) setErrors((prev) => ({ ...prev, image: undefined }));
            }}
            placeholder="https://example.com/logo.jpg"
            className={`${styles.input} ${errors.image ? styles.inputError : ''}`}
          />
          {errors.image && <p className={styles.error}>{errors.image}</p>}
          {image && (
            <button type="button" className={styles.clearUrlBtn} onClick={() => setImage(null)}>
              Clear image
            </button>
          )}
        </div>
      </section>

      <div className={styles.field}>
        <span className={styles.label}>Status</span>
        <div className={styles.segmented} role="group" aria-label="Vendor status">
          <button
            type="button"
            className={`${styles.segment} ${status === 'active' ? styles.segmentActive : ''}`}
            onClick={() => setStatus('active')}
          >
            Active
          </button>
          <button
            type="button"
            className={`${styles.segment} ${status === 'inactive' ? styles.segmentPaused : ''}`}
            onClick={() => setStatus('inactive')}
          >
            Paused
          </button>
        </div>
      </div>

      {!hideFooter && (
        <div className={styles.actions}>
          <button type="button" onClick={onCancel} className={`${styles.button} ${styles.buttonSecondary}`}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid}
            className={`${styles.button} ${styles.buttonPrimary}`}
          >
            Save changes
          </button>
        </div>
      )}
    </form>
  );
};

export default VendorDetailsForm;
